import type { EvidenceProps } from './registry';

interface GoalRecord {
  id?: string | number;
  name?: string;
  attributionModel: string;
  type?: string;
  category?: string;
}

interface MicrosoftAttributionEvidenceShape {
  platform: 'microsoft_ads';
  totalGoals: number;
  activeCount: number;
  onDdaCount: number;
  onDda: GoalRecord[];
  notOnDdaCount: number;
  notOnDda: GoalRecord[];
}

// Microsoft attribution-model enum → human label.
// Differences from Google's enum: Microsoft uses simpler names (no GOOGLE_*_ prefixes).
const MODEL_LABEL: Record<string, string> = {
  DataDriven: 'Data-Driven',
  LastClick: 'Last click',
  LinearTouch: 'Linear',
  TimeDecay: 'Time decay',
  PositionBased: 'Position-based',
  ExternallyAttributed: 'External',
  UNKNOWN: 'Unknown'
};

// Microsoft conversion-goal Type → human label.
const TYPE_LABEL: Record<string, string> = {
  Url: 'URL',
  Event: 'Event',
  AppInstall: 'App install',
  Duration: 'Duration',
  PagesViewedPerVisit: 'Pages per visit',
  OfflineConversion: 'Offline',
  InStoreTransaction: 'In-store transaction',
  Product: 'Product',
  CustomEvent: 'Custom event',
  MultiStage: 'Multi-stage',
  InStoreVisit: 'In-store visit'
};

function label(map: Record<string, string>, key: string | undefined): string {
  if (!key) return '—';
  return map[key] ?? key;
}

export const AttributionModelMicrosoftEvidence = ({ evidence }: EvidenceProps) => {
  const e = evidence as unknown as MicrosoftAttributionEvidenceShape;

  return (
    <div className='evidence'>
      <p className='evidence__lead'>
        <strong>{e.totalGoals}</strong> conversion goal{e.totalGoals === 1 ? '' : 's'} configured (
        {e.activeCount} Active). <strong>{e.onDdaCount}</strong> on Data-Driven ·{' '}
        <strong>{e.notOnDdaCount}</strong> on other attribution models.
      </p>

      {e.notOnDdaCount > 0 && <ActionableSection goals={e.notOnDda} count={e.notOnDdaCount} />}
      {e.onDdaCount > 0 && <OnDdaSection goals={e.onDda} count={e.onDdaCount} />}
      {e.notOnDdaCount === 0 && e.onDdaCount > 0 && (
        <p className='evidence__footnote'>
          All {e.onDdaCount} active goal{e.onDdaCount === 1 ? '' : 's'} are using Data-Driven
          Attribution.
        </p>
      )}
    </div>
  );
};

const ActionableSection = ({ goals, count }: { goals: GoalRecord[]; count: number }) => (
  <section className='evidence__section'>
    <h4 className='evidence__section-title'>
      Active goals not on Data-Driven
      <span className='evidence__section-tag evidence__section-tag--actionable'>
        {count} actionable
      </span>
    </h4>
    <p className='evidence__section-lead'>
      These active goals are using non-DDA attribution. Microsoft allows DDA selection directly — if
      the goal has enough volume, switching is a structural fix. Unlike Google, Microsoft doesn't
      expose a per-goal "DDA available" status field; you discover eligibility by attempting to save
      the change.
    </p>
    <GoalTable goals={goals} />
  </section>
);

const OnDdaSection = ({ goals, count }: { goals: GoalRecord[]; count: number }) => (
  <section className='evidence__section'>
    <h4 className='evidence__section-title'>
      Currently on Data-Driven Attribution
      <span className='evidence__section-tag evidence__section-tag--ok'>{count} on track</span>
    </h4>
    <GoalTable goals={goals} />
  </section>
);

const GoalTable = ({ goals }: { goals: GoalRecord[] }) => (
  <table className='evidence__table evidence__table--microsoft-goals'>
    <thead>
      <tr>
        <th>Conversion goal</th>
        <th>Type</th>
        <th>Attribution model</th>
        <th className='evidence__th--right'>Goal ID</th>
      </tr>
    </thead>
    <tbody>
      {goals.map((g, i) => (
        <tr key={g.id ?? `${g.name ?? '(unnamed)'}-${i}`}>
          <td>{g.name ?? '(unnamed)'}</td>
          <td>{label(TYPE_LABEL, g.type)}</td>
          <td>{label(MODEL_LABEL, g.attributionModel)}</td>
          <td className='evidence__td--right'>{g.id ?? '—'}</td>
        </tr>
      ))}
    </tbody>
  </table>
);
